import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { HEAD_ACTION } from '@/components/admin/sessions/export-menu';
import { NPC_ENTRY_HINT, type NpcEntrySession, npcEntryState } from '@/lib/npc-create-entry';
import { Button, Icon } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';
import { useId } from 'react';

/**
 * « Créer un NPC » - the gesture of the head of a finished session that turns its words into a new
 * NPC (npc.md §4.2, §13.3, H18; maquette `DepuisSession`), between « Exporter en » and
 * « Supprimer ».
 *
 * ## Three shapes, decided by the DETAIL alone
 *
 * - the NPC do not exist here (the replay is closed, or this is the vitrine): NOTHING is drawn;
 * - the session cannot give one - a replay, one still running, one with no protocol page or no turn,
 *   or one in the trash: a package `Button` that is really `disabled`, with its reason
 *   written under it, the régime of « Relecture » on a session in progress. The reason is the one
 *   the server would refuse with (`npc-create-entry.ts`);
 * - otherwise a link to `/admin/npc/nouveau?depuis=<session>`, the page that reads the prefill,
 *   with the line that says what it does. A session of the prod brought up to the dev carries the
 *   same link: the NPC is born on the dev, the session of the prod is not touched.
 *
 * It is the router's own `Link`, typed against the tree: a real `href`, the middle click, and a
 * left click that does not reload the SPA.
 */

export interface CreateNpcButtonProps {
  readonly detail: NpcEntrySession & { readonly session: { readonly id: string } };
  /** Do the NPC exist on this server: the replay open, and not the vitrine. */
  readonly available: boolean;
}

/** The sentence under the button: narrow, flush right, as the maquette draws it. */
const REASON_SHAPE = 'max-w-[30ch] text-right text-gray-600 text-caption';

export function CreateNpcButton({ detail, available }: CreateNpcButtonProps) {
  const reasonId = useId();
  const state = npcEntryState(detail, available);
  if (state.kind === 'absent') return null;

  if (state.kind === 'disabled') {
    return (
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <Button
          type="button"
          variant="accent"
          size="sm"
          compact={true}
          icon="theater_comedy"
          disabled
          aria-describedby={reasonId}
          data-testid={SESSIONS_TEST_IDS.createNpc}
          data-state="disabled"
          data-reason={state.reason}
        >
          Créer un NPC
        </Button>
        <p id={reasonId} data-testid={SESSIONS_TEST_IDS.createNpcReason} className={REASON_SHAPE}>
          {state.sentence}
        </p>
      </div>
    );
  }

  const hintId = reasonId;
  return (
    <div className="flex shrink-0 flex-col items-end gap-1.5">
      <Link
        to="/admin/npc/nouveau"
        search={{ depuis: detail.session.id }}
        aria-describedby={hintId}
        data-testid={SESSIONS_TEST_IDS.createNpc}
        data-state="open"
        className={HEAD_ACTION}
      >
        <Icon name="theater_comedy" size={16} />
        Créer un NPC
      </Link>
      <p id={hintId} data-testid={SESSIONS_TEST_IDS.createNpcHint} className={REASON_SHAPE}>
        {NPC_ENTRY_HINT}
      </p>
    </div>
  );
}
